import { inject, Injectable } from '@angular/core';
import { StartWithUnit } from '@core/state/operators/start-with-unit';
import { Load } from '@api/models/B2bService/Contract/load';
import { B2BService } from '@api/services/b-2-b.service';
import { ApiB2BListB2BLoadGet$Params } from '@api/fn/b-2-b/api-b-2-b-list-b-2-b-load-get';
import { startWithLoading } from '@core/state/operators/start-with-loading';
import { Observable } from 'rxjs';
import { RxActionFactory, RxActions } from '@rx-angular/state/actions';
import { ApiB2BB2BAddFilePost$Params } from '@api/fn/b-2-b/api-b-2-bb-2-b-add-file-post';
import { ApiB2BB2BRemoveFilePost$Params } from '@api/fn/b-2-b/api-b-2-bb-2-b-remove-file-post';
import { B2BFile } from '@api/models/B2bService/Contract/b-2-b-file';
import {
    UploadRidersFileType,
} from '../../features/program/program-uploads/b2b-program-file-upload/b2b-program-file-upload.component';
import { B2bProgramType } from '../models/b2b-program-type';
import {
    B2BTicketsetLoadListResponse,
} from '@api/models/B2bService/Contract/LoadTickets/b-2-b-ticketset-load-list-response';
import {
    B2BTicketsetLogfileGetResponse,
} from '@api/models/B2bService/Contract/LoadTickets/b-2-b-ticketset-logfile-get-response';
import { CustomRxState } from '@core/state/rx.state';


export type LoadEntity = Load | B2BTicketsetLoadListResponse;

interface IB2bLoadsState {
    loading: Record<StartWithUnit, boolean>;
    loads: LoadEntity[];
    prgType: B2bProgramType | null;
}

interface B2bLoadsAction {
    requestLoads: LoadEntity[];
    setPrgType: B2bProgramType;
}

@Injectable({
    providedIn: 'root',
})
export class B2bLoadsState extends CustomRxState<IB2bLoadsState> {
    readonly #b2BService: B2BService = inject(B2BService);
    readonly #actions: RxActions<B2bLoadsAction> = new RxActionFactory<B2bLoadsAction>().create();

    public readonly loads$: Observable<LoadEntity[]> = this.select('loads');
    public readonly loading$: Observable<Record<StartWithUnit, boolean>> = this.select('loading');


    constructor() {
        super();

        this.setDefaultState();
        this.connectSelectors();
    }

    private setDefaultState(): void {
        this.set({
            loads: null,
            loading: null,
            prgType: null,
        });
    }

    private connectSelectors(): void {
        this.connect('loads', this.#actions.requestLoads$);
        this.connect('prgType', this.#actions.setPrgType$);
    }

    public setProgramType(prgType: B2bProgramType): void {
        this.#actions.setPrgType(prgType);
        this.#actions.requestLoads(null);
    }

    public requestLoads(body: ApiB2BListB2BLoadGet$Params, prgType: B2bProgramType): void {
        this.setProgramType(prgType);

        const request$: Observable<LoadEntity[]> = prgType === B2bProgramType.NumberOfTrips
            ? this.#b2BService.apiB2BB2BTicketsetLoadListGet(body)
            : this.#b2BService.apiB2BListB2BLoadGet(body);

        this.hold(
            request$.pipe(
                startWithLoading(this, StartWithUnit.RequestB2BProgramLoads),
            ),
            this.#actions.requestLoads,
        );
    }

    public requestUploadFileRiders(
        prgId: number,
        prgType: B2bProgramType,
        file: Blob,
        type: UploadRidersFileType,
    ): Observable<B2BFile> {
        if (type === UploadRidersFileType.Add) {
            return prgType === B2bProgramType.NumberOfTrips ? this.requestAddNumberTripsRiders({
                prgId,
                body: { file },
            }) : this.requestAddRiders({ prgId, body: { file } });
        } else if (type === UploadRidersFileType.Remove) {
            return prgType === B2bProgramType.NumberOfTrips ? this.requestDeleteNumberTripsRiders({
                prgId,
                body: { file },
            }) : this.requestDeleteRiders({ prgId, body: { file } });
        }
    }

    public requestReportErrorParsingB2bPrgNumberOfTrips(fileSid: string): Observable<B2BTicketsetLogfileGetResponse> {
        return this.#b2BService.apiB2BB2BTicketsetLogfileGetGet({ fileSid });
    }

    public requestReportErrorDbB2bPrgNumberOfTrips(fileSid: string): Observable<B2BTicketsetLogfileGetResponse> {
        return this.#b2BService.apiB2BB2BTicketsetLogsListGet({ fileSid, hasError: 2 });
    }

    private requestAddRiders(body: ApiB2BB2BAddFilePost$Params): Observable<B2BFile> {
        return this.#b2BService.apiB2BB2BAddFilePost(body).pipe(
            startWithLoading(this, StartWithUnit.RequestB2BProgramUploadFile),
        );
    }

    private requestAddNumberTripsRiders(body: ApiB2BB2BAddFilePost$Params): Observable<B2BFile> {
        return this.#b2BService.apiB2BB2BAddTicketsFilePost(body).pipe(
            startWithLoading(this, StartWithUnit.RequestB2BProgramUploadFile),
        );
    }

    private requestDeleteRiders(body: ApiB2BB2BRemoveFilePost$Params): Observable<B2BFile> {
        return this.#b2BService.apiB2BB2BRemoveFilePost(body).pipe(
            startWithLoading(this, StartWithUnit.RequestB2BProgramUploadFile),
        );
    }

    private requestDeleteNumberTripsRiders(body: ApiB2BB2BAddFilePost$Params): Observable<B2BFile> {
        return this.#b2BService.apiB2BB2BRemoveTicketsFilePost(body).pipe(
            startWithLoading(this, StartWithUnit.RequestB2BProgramUploadFile),
        );
    }
}
